import type { useMutation } from "@apollo/client/react";

type MutationFunction = ReturnType<typeof useMutation>[0];
import _ from "lodash";
import { StoreApi, UseBoundStore } from "zustand";
import { createWithEqualityFn } from "zustand/traditional";

import { IObject } from "@/__generated__/graphql";
import { NodeStore } from "@/components/@types";
import { ObjectFieldDefinitionType, ObjectType } from "@/objects/@types";
import { generateMissingKeyValuesForOptimisticResponse } from "@/objects/@utils";
import { State } from "@/state";

type ObjectsRegistry = typeof import("@/objects").Objects;

let objectsRegistry: ObjectsRegistry | undefined;
let objectsRegistryPromise: Promise<ObjectsRegistry> | undefined;

/** Deferred ESM import breaks Objects → DocumentObject → Node.hooks → Node.store cycle. */
export function ensureObjectsRegistry(): Promise<ObjectsRegistry> {
  if (objectsRegistry) return Promise.resolve(objectsRegistry);
  if (!objectsRegistryPromise) {
    objectsRegistryPromise = import("@/objects").then((mod) => {
      objectsRegistry = mod.Objects;
      return objectsRegistry;
    });
  }
  return objectsRegistryPromise;
}

function getObjects(): ObjectsRegistry {
  if (!objectsRegistry) {
    throw new Error("Objects registry not loaded");
  }
  return objectsRegistry;
}

void ensureObjectsRegistry();

export type ArrayRowRecord = Record<string, unknown> & {
  identifier?: string;
  id?: string;
  metadata?: { _frontend_uuid?: string } | null;
};

/**
 * Pending field ops. ``submitBatchId`` is stamped when a submit begins so a
 * later server response can drop exactly the ops that were in that submit
 * and keep only edits that arrived while the request was in flight.
 */
export type NodeFieldOperation =
  | {
      opId: string;
      fieldKey: string;
      type: "scalar.set";
      value: unknown;
      submitBatchId?: string;
    }
  | {
      opId: string;
      fieldKey: string;
      type: "array.remove";
      rowKey: string;
      submitBatchId?: string;
    }
  | {
      opId: string;
      fieldKey: string;
      type: "array.upsert";
      rowKey: string;
      row: ArrayRowRecord;
      submitBatchId?: string;
    }
  | {
      opId: string;
      fieldKey: string;
      type: "array.reorder";
      rowKeys: string[];
      submitBatchId?: string;
    };

function newOpId(): string {
  return crypto.randomUUID();
}

/**
 * Stable row identity for array ops.
 *
 * Prefer ``identifier`` when present (server truth). Otherwise use the
 * client-minted ``metadata._frontend_uuid``. Never invent a new uuid for a
 * row that already has either — reminting is what caused row multiplication.
 */
export function getArrayRowKey(
  row: ArrayRowRecord | null | undefined,
): string | undefined {
  if (row == null || typeof row !== "object") return undefined;
  if (typeof row.identifier === "string" && row.identifier) {
    return row.identifier;
  }
  const frontendUuid = row.metadata?._frontend_uuid;
  if (typeof frontendUuid === "string" && frontendUuid) return frontendUuid;
  if (typeof row.id === "string" && row.id) return row.id;
  return undefined;
}

export function ensureArrayRowKeys(rows: ArrayRowRecord[]): ArrayRowRecord[] {
  return rows.map((row) => {
    if (getArrayRowKey(row)) {
      // Persist identifier into metadata so later diffs keep the same key
      // even if a transient view drops ``identifier``.
      if (
        row.identifier &&
        row.metadata?._frontend_uuid !== row.identifier &&
        !row.metadata?._frontend_uuid
      ) {
        return {
          ...row,
          metadata: {
            ...(row.metadata ?? {}),
            _frontend_uuid: row.identifier,
          },
        };
      }
      return row;
    }
    const frontendUuid = crypto.randomUUID();
    return {
      ...row,
      metadata: {
        ...(row.metadata ?? {}),
        _frontend_uuid: frontendUuid,
      },
    };
  });
}

/** Hydrate server rows with a stable client key without reminting. */
export function stabilizeServerArrayRows(
  rows: ArrayRowRecord[],
): ArrayRowRecord[] {
  return rows.map((row) => {
    if (row.metadata?._frontend_uuid) return row;
    if (typeof row.identifier === "string" && row.identifier) {
      return {
        ...row,
        metadata: {
          ...(row.metadata ?? {}),
          _frontend_uuid: row.identifier,
        },
      };
    }
    return row;
  });
}

export function createSetOperation(
  fieldKey: string,
  value: unknown,
): NodeFieldOperation {
  return { opId: newOpId(), fieldKey, type: "scalar.set", value };
}

export function createSetOperations(
  patch: Record<string, unknown>,
): NodeFieldOperation[] {
  return Object.entries(patch).map(([fieldKey, value]) =>
    createSetOperation(fieldKey, value),
  );
}

export function diffArrayRows(
  fieldKey: string,
  previousValue: unknown,
  nextValue: unknown,
): NodeFieldOperation[] {
  const prevRows = ensureArrayRowKeys(
    Array.isArray(previousValue) ? (previousValue as ArrayRowRecord[]) : [],
  );
  const nextRows = ensureArrayRowKeys(
    Array.isArray(nextValue) ? (nextValue as ArrayRowRecord[]) : [],
  );

  const prevByKey = new Map<string, ArrayRowRecord>();
  for (const row of prevRows) {
    const key = getArrayRowKey(row);
    if (key) prevByKey.set(key, row);
  }

  const nextByKey = new Map<string, ArrayRowRecord>();
  const nextKeys: string[] = [];
  for (const row of nextRows) {
    const key = getArrayRowKey(row);
    if (!key) continue;
    nextByKey.set(key, row);
    nextKeys.push(key);
  }

  const ops: NodeFieldOperation[] = [];

  for (const key of prevByKey.keys()) {
    if (!nextByKey.has(key)) {
      ops.push({
        opId: newOpId(),
        fieldKey,
        type: "array.remove",
        rowKey: key,
      });
    }
  }

  for (const [key, row] of nextByKey) {
    const prevRow = prevByKey.get(key);
    if (!prevRow || !_.isEqual(prevRow, row)) {
      ops.push({
        opId: newOpId(),
        fieldKey,
        type: "array.upsert",
        rowKey: key,
        row,
      });
    }
  }

  const prevKeys = prevRows
    .map((row) => getArrayRowKey(row))
    .filter((key): key is string => Boolean(key));

  if (!_.isEqual(prevKeys, nextKeys)) {
    ops.push({
      opId: newOpId(),
      fieldKey,
      type: "array.reorder",
      rowKeys: nextKeys,
    });
  }

  return ops;
}

function rowMatchesKey(row: ArrayRowRecord, rowKey: string): boolean {
  if (!rowKey) return false;
  return (
    row.identifier === rowKey ||
    row.metadata?._frontend_uuid === rowKey ||
    row.id === rowKey
  );
}

function mergeArrayRow(
  serverRow: ArrayRowRecord,
  queuedRow: ArrayRowRecord,
): ArrayRowRecord {
  const merged = {
    ...serverRow,
    ...queuedRow,
    // Server identifier wins once assigned.
    identifier: serverRow.identifier ?? queuedRow.identifier,
  };
  const metadata = {
    ...(queuedRow.metadata ?? {}),
    ...(serverRow.metadata ?? {}),
  };
  if (
    !metadata._frontend_uuid &&
    typeof merged.identifier === "string" &&
    merged.identifier
  ) {
    metadata._frontend_uuid = merged.identifier;
  }
  if (Object.keys(metadata).length > 0) {
    merged.metadata = metadata;
  }
  return merged;
}

function applyArrayOperations(
  serverRows: ArrayRowRecord[],
  operations: NodeFieldOperation[],
): ArrayRowRecord[] {
  let rows = stabilizeServerArrayRows(serverRows);

  for (const op of operations) {
    switch (op.type) {
      case "array.remove":
        rows = rows.filter((row) => !rowMatchesKey(row, op.rowKey));
        break;
      case "array.upsert": {
        const index = rows.findIndex((row) => rowMatchesKey(row, op.rowKey));
        if (index >= 0) {
          rows[index] = mergeArrayRow(rows[index], op.row);
        } else if (op.row.identifier) {
          const byIdentifier = rows.findIndex(
            (row) => row.identifier === op.row.identifier,
          );
          if (byIdentifier >= 0) {
            rows[byIdentifier] = mergeArrayRow(rows[byIdentifier], op.row);
          } else {
            rows.push(ensureArrayRowKeys([op.row])[0]);
          }
        } else {
          rows.push(ensureArrayRowKeys([op.row])[0]);
        }
        break;
      }
      case "array.reorder": {
        const remaining = [...rows];
        const ordered: ArrayRowRecord[] = [];
        for (const key of op.rowKeys) {
          const index = remaining.findIndex((row) => rowMatchesKey(row, key));
          if (index >= 0) {
            ordered.push(remaining[index]);
            remaining.splice(index, 1);
          }
        }
        ordered.push(...remaining);
        rows = ordered;
        break;
      }
      default:
        break;
    }
  }

  return rows;
}

export function applyPendingOperations(
  serverState: Record<string, unknown>,
  operations: NodeFieldOperation[],
): Record<string, unknown> {
  const result = { ...serverState };
  const byField = new Map<string, NodeFieldOperation[]>();
  for (const op of operations) {
    const list = byField.get(op.fieldKey) ?? [];
    list.push(op);
    byField.set(op.fieldKey, list);
  }

  for (const [fieldKey, fieldOps] of byField) {
    const serverValue = _.get(serverState, fieldKey);
    const hasArrayOps = fieldOps.some((op) => op.type.startsWith("array."));

    if (hasArrayOps) {
      const serverRows = Array.isArray(serverValue)
        ? (serverValue as ArrayRowRecord[])
        : [];
      _.set(result, fieldKey, applyArrayOperations(serverRows, fieldOps));
      continue;
    }

    for (const op of fieldOps) {
      if (op.type === "scalar.set") {
        _.set(result, fieldKey, op.value);
      }
    }
  }

  return result;
}

/**
 * Stamp every currently-pending op with a submit batch id. Ops created after
 * this call stay untagged (post-submit edits).
 */
export function markPendingOperationsForSubmit(
  operations: NodeFieldOperation[],
): { batchId: string; operations: NodeFieldOperation[] } {
  const batchId = crypto.randomUUID();
  return {
    batchId,
    operations: operations.map((op) =>
      op.submitBatchId ? op : { ...op, submitBatchId: batchId },
    ),
  };
}

function isRemoveAckedByServer(
  op: Extract<NodeFieldOperation, { type: "array.remove" }>,
  serverState: Record<string, unknown>,
): boolean {
  const serverValue = _.get(serverState, op.fieldKey);
  const serverRows = Array.isArray(serverValue)
    ? (serverValue as ArrayRowRecord[])
    : [];
  return !serverRows.some((row) => rowMatchesKey(row, op.rowKey));
}

function isUpsertAckedByServer(
  op: Extract<NodeFieldOperation, { type: "array.upsert" }>,
  serverState: Record<string, unknown>,
): boolean {
  const serverValue = _.get(serverState, op.fieldKey);
  const serverRows = Array.isArray(serverValue)
    ? (serverValue as ArrayRowRecord[])
    : [];
  const index = serverRows.findIndex((row) => rowMatchesKey(row, op.rowKey));
  const matched =
    index >= 0
      ? serverRows[index]
      : op.row.identifier
        ? serverRows.find((row) => row.identifier === op.row.identifier)
        : undefined;
  if (!matched) return false;

  const ignored = new Set([
    "identifier",
    "id",
    "__typename",
    "metadata",
    "createdAt",
    "updatedAt",
  ]);
  return Object.entries(op.row).every(([key, value]) => {
    if (ignored.has(key)) return true;
    return _.isEqual(matched[key], value);
  });
}

export function isOperationAckedByServer(
  op: NodeFieldOperation,
  serverState: Record<string, unknown>,
): boolean {
  const serverValue = _.get(serverState, op.fieldKey);
  switch (op.type) {
    case "scalar.set":
      return _.isEqual(serverValue, op.value);
    case "array.remove":
      return isRemoveAckedByServer(op, serverState);
    case "array.upsert":
      return isUpsertAckedByServer(op, serverState);
    case "array.reorder": {
      const serverRows = Array.isArray(serverValue)
        ? (serverValue as ArrayRowRecord[])
        : [];
      const expected = op.rowKeys.filter((key) =>
        serverRows.some((row) => rowMatchesKey(row, key)),
      );
      const actualOrder: string[] = [];
      for (const row of serverRows) {
        const match = op.rowKeys.find((rowKey) => rowMatchesKey(row, rowKey));
        if (match) actualOrder.push(match);
      }
      return _.isEqual(actualOrder, expected);
    }
    default:
      return false;
  }
}

/**
 * Take the server snapshot as the new baseline, keep applying any ops the
 * server has not absorbed yet, and drop only ops that are actually reflected.
 *
 * Critical for deletes: a tagged ``array.remove`` must stay applied until the
 * server snapshot no longer contains that row — otherwise a stale response
 * resurrects it.
 *
 * Tagged non-remove ops that are not yet matchable on the server are dropped
 * (the submit already sent them; re-applying unmatched upserts duplicates rows).
 */
export function reconcileServerStateWithPendingOperations(
  serverState: Record<string, unknown>,
  operations: NodeFieldOperation[],
): {
  fieldValues: Record<string, unknown>;
  pendingFieldOperations: NodeFieldOperation[];
} {
  const remaining = operations.filter((op) => {
    if (isOperationAckedByServer(op, serverState)) {
      return false;
    }
    // Submitted removes must keep applying until the row is gone.
    if (op.type === "array.remove") {
      return true;
    }
    // Other submitted ops: drop once tagged — server membership is authoritative
    // and unmatched upserts must not be replayed (duplicates).
    if (op.submitBatchId) {
      return false;
    }
    return true;
  });

  return {
    fieldValues: applyPendingOperations(serverState, remaining),
    pendingFieldOperations: remaining,
  };
}

export function buildFieldChangeOperations(
  fieldKey: string,
  fieldType: string | undefined,
  previousValue: unknown,
  nextValue: unknown,
): NodeFieldOperation[] {
  // ``table`` is the current panel field type for editable row arrays.
  if (fieldType === "table") {
    return diffArrayRows(fieldKey, previousValue, nextValue);
  }
  return [createSetOperation(fieldKey, nextValue)];
}

/** Hold server→``fieldValues`` reconcile while the user is mid-gesture. */
export function beginNodeInteraction(
  store: UseBoundStore<StoreApi<NodeStore>>,
) {
  store.setState({
    interactionLockCount: store.getState().interactionLockCount + 1,
  });
}

/**
 * Release one interaction hold. The decrement (and epoch bump that
 * re-runs server reconcile) is deferred a microtask so a cell remount
 * or focus handoff can ``begin`` again before the lock drops to zero.
 */
export function endNodeInteraction(store: UseBoundStore<StoreApi<NodeStore>>) {
  queueMicrotask(() => {
    const count = Math.max(0, store.getState().interactionLockCount - 1);
    store.setState({ interactionLockCount: count });
    if (count === 0) {
      store.setState({
        interactionEpoch: store.getState().interactionEpoch + 1,
      });
    }
  });
}

export function createNodeFieldsStore(
  mode: "create" | "update" | "get",
  record?: Pick<NodeStore, "objectType" | "typename" | "fieldValues">,
) {
  const store = createWithEqualityFn<NodeStore>(() => ({
    mode: mode,
    fieldDefinitions: [],
    fieldValues: {},
    fieldValuesInitial: {},
    pendingFieldOperations: [],
    interactionLockCount: 0,
    interactionEpoch: 0,
    objectType: null,
    typename: null,
    entityIdentifier: null,
    pendingChange: null,
    missingRequiredRevealCount: 0,
    groupViews: {},
  }));

  // Made holding a record where the objects are in, its fields are set up
  // with it before anything reads it. Set up by the subscriber below, a
  // microtask later, whatever read it was drawn first without them and then
  // again - a table's every cell, on every row scrolled to.
  if (record && objectsRegistry) {
    store.setState({ ...record, fieldValuesInitial: record.fieldValues });
    store.setState({
      fieldDefinitions: getFieldDefinitionsFromNode(
        store,
        record.fieldValues,
        record.typename!,
      ),
    });
  }

  store.subscribe((state, prev) => {
    const valuesChanged = state.fieldValues !== prev.fieldValues;
    const typenameChanged = state.typename !== prev.typename;
    const objectTypeChanged = state.objectType !== prev.objectType;
    if (!valuesChanged && !typenameChanged && !objectTypeChanged) return;
    if (state.typename == null || state.objectType == null) return;

    void ensureObjectsRegistry().then(() => {
      const nextDefs = getFieldDefinitionsFromNode(
        store,
        state.fieldValues as Record<string, unknown>,
        state.typename!,
      );
      const prevDefs = store.getState().fieldDefinitions;
      const sameShape =
        !typenameChanged &&
        prevDefs.length === nextDefs.length &&
        prevDefs.every(
          (p, i) =>
            p.objectKey === nextDefs[i].objectKey &&
            p.isEditable === nextDefs[i].isEditable &&
            p.isRequired === nextDefs[i].isRequired,
        );
      if (sameShape) return;
      store.setState({ fieldDefinitions: nextDefs });
    });
  });

  if (record && !objectsRegistry) {
    store.setState({ ...record, fieldValuesInitial: record.fieldValues });
  }

  return store;
}

export function getFieldDefinitionsFromNode(
  store: UseBoundStore<StoreApi<NodeStore>>,
  node: any,
  typename: string,
  defaultEntityKey: string = "entity",
) {
  const objectDefinition = getObjects()[
    typename as keyof ObjectsRegistry
  ] as ObjectType<IObject>;

  const mode = store.getState().mode;
  const objectFieldDefinitions = objectDefinition?.fieldDefinitions || [];

  const liveData = { ...(node ?? {}), ...store.getState().fieldValues };
  const resolveProps = <P>(
    raw: P | ((data: any) => P) | undefined,
  ): P | undefined =>
    typeof raw === "function" ? (raw as (d: any) => P)(liveData) : raw;
  const resolveFlag = (
    flag: boolean | ((data: any) => boolean) | undefined,
    fallback: boolean,
  ): boolean =>
    typeof flag === "function"
      ? flag(liveData)
      : typeof flag === "boolean"
        ? flag
        : fallback;

  return objectFieldDefinitions
    .map((fieldDefinition) => ({
      fieldDefinition,
      createProps: resolveProps(fieldDefinition.createProps),
      updateProps: resolveProps(fieldDefinition.updateProps),
    }))
    .filter(({ createProps, updateProps }) => {
      const hasRow =
        (mode === "create" && createProps) ||
        (mode === "update" && updateProps) ||
        (mode === "get" && updateProps);
      if (!hasRow) return false;
      const visible =
        mode === "create"
          ? resolveFlag(createProps?.isVisible, true)
          : resolveFlag(updateProps?.isVisible, true);
      return visible;
    })
    .map(({ fieldDefinition, createProps, updateProps }) => ({
      groupId: fieldDefinition.groupId,
      objectKey: fieldDefinition.objectKey,
      inputKey: fieldDefinition?.inputKey || fieldDefinition.objectKey,
      valueKeys: [
        ...(fieldDefinition?.linkedValuesKeys || [fieldDefinition.objectKey]),
        fieldDefinition.entityKey || defaultEntityKey,
      ],
      asString: fieldDefinition.asString,
      label: fieldDefinition.label,
      isEditable:
        mode === "get"
          ? false
          : mode === "update"
            ? resolveFlag(updateProps?.isEditable, false)
            : true,
      // Required only where an object says so: defaulting to required held
      // submit back on fields nothing marked, with nothing to say why.
      isRequired:
        mode === "get"
          ? false
          : mode === "create"
            ? resolveFlag(createProps?.isRequired, false)
            : resolveFlag(updateProps?.isRequired, false),
      isFullWidth:
        mode === "create"
          ? createProps?.isFullWidth === true
          : updateProps?.isFullWidth === true,
      type: fieldDefinition.type,
      hasExplicitGetValueForApi: Boolean(fieldDefinition?.getValueForApi),
      listInputFormat:
        fieldDefinition.type === "table"
          ? fieldDefinition.listInputFormat
          : undefined,
      getValue: fieldDefinition.getValue,
      getValueForApi:
        fieldDefinition?.getValueForApi || fieldDefinition.getValue,
      getValueOptions: fieldDefinition?.getValueOptions,
      ReactIcon: fieldDefinition.ReactIcon,
    })) as ObjectFieldDefinitionType<IObject>[];
}

export async function submitNodeToGraphQLServer(
  store: UseBoundStore<StoreApi<NodeStore>>,
  mutate: MutationFunction,
) {
  const objects = await ensureObjectsRegistry();
  const fieldDefinitions = store.getState().fieldDefinitions;
  const fieldValues = store.getState().fieldValues;
  const fieldValuesInitial = store.getState().fieldValuesInitial;
  const optimisticFieldValues = { ...fieldValues };

  const nodeId = fieldValues.id as string;
  const typename = fieldValues.__typename as string;
  const objectDefinition = objects[typename as keyof ObjectsRegistry];
  const inputKeyPath: string[] = (() => {
    const raw = objectDefinition?.inputKey;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") return [raw];
    return [_.lowerFirst(typename.replace("Object", ""))];
  })();

  const mode = store.getState().mode;
  if (mode === "get") {
    return;
  }

  if (!_.isEqual(fieldValuesInitial, fieldValues)) {
    const { batchId, operations } = markPendingOperationsForSubmit(
      store.getState().pendingFieldOperations,
    );
    store.setState({ pendingFieldOperations: operations });
    // What this save puts on the server, captured before it leaves. Editing
    // continues while it is in flight, so neither the baseline nor the pending
    // queue can be taken from the store when the response lands.
    const submittedFieldValues = _.cloneDeep(store.getState().fieldValues);

    const initialEditableByObjectKey = (() => {
      const objectDef = objects[typename as keyof ObjectsRegistry];
      const out: Record<string, boolean> = {};
      for (const fd of objectDef?.fieldDefinitions ?? []) {
        const updateProps =
          typeof fd.updateProps === "function"
            ? (fd.updateProps as (d: any) => any)(fieldValuesInitial)
            : fd.updateProps;
        const flag = updateProps?.isEditable;
        const resolved =
          typeof flag === "function"
            ? (flag as (d: any) => boolean)(fieldValuesInitial)
            : typeof flag === "boolean"
              ? flag
              : false;
        out[fd.objectKey as string] = resolved;
      }
      return out;
    })();
    const values = fieldDefinitions.reduce((acc, fieldDefinition) => {
      const wasEditableInitially =
        initialEditableByObjectKey[fieldDefinition.objectKey] ?? false;
      if (!fieldDefinition.isEditable && !wasEditableInitially) {
        return acc;
      }

      const getValue =
        fieldDefinition.getValueForApi ||
        (fieldDefinition.getValue as (value: any) => any) ||
        ((value) => value);

      const initialValue = getValue(fieldValuesInitial);
      const currentValue = getValue(fieldValues);

      if (!_.isEqual(initialValue, currentValue)) {
        if (fieldDefinition.type === "reference") {
          const identifier = currentValue?.identifier;
          acc[fieldDefinition.inputKey] = { identifier };
        } else if (fieldDefinition.type === "references") {
          // A field that says its own API value (a report's entityIdentifiers
          // and shareEmails, lists of strings) is sent as it says. Any other is
          // a list of records, sent as { nodes }: an empty one included, which
          // is how the last of them is taken off - told apart by what it held,
          // an empty list went bare and the API refused the save.
          acc[fieldDefinition.inputKey] =
            "hasExplicitGetValueForApi" in fieldDefinition &&
            fieldDefinition.hasExplicitGetValueForApi
              ? currentValue
              : {
                  nodes: currentValue.map((reference) => ({
                    identifier: reference.identifier,
                  })),
                };
        } else if (fieldDefinition.type === "tags") {
          acc[fieldDefinition.inputKey] = {
            nodes: currentValue.map((tag) => ({
              name: tag.name,
              identifier: tag.identifier,
              color: tag.color,
            })),
          };

          optimisticFieldValues[fieldDefinition.objectKey] = currentValue.map(
            (tag) => {
              if (tag.identifier != null) return tag;
              const { id, identifier, createdAt, updatedAt } =
                generateMissingKeyValuesForOptimisticResponse("TagObject");
              return {
                ...tag,
                __typename: "TagObject",
                id,
                identifier,
                createdAt,
                updatedAt,
              };
            },
          );
        } else if (fieldDefinition.type === "table") {
          const { columns } = fieldDefinition.getValueOptions(currentValue);
          const initialRows = Array.isArray(initialValue) ? initialValue : [];
          const persistedIdentifiers = new Set(
            initialRows.map((row) => row?.identifier).filter(Boolean),
          );

          const nodes = currentValue.map((row) => {
            const mappedNode = columns.reduce((node, column) => {
              const key = column?.inputKey || column.objectKey;
              const apiValue = column?.getValueForApi
                ? column?.getValueForApi(row)
                : row?.[column.objectKey];
              // ``undefined`` marks a display-only column — one showing a value
              // the API computes and has no input field for (a line's tax
              // amount, say). Omit the key instead of sending it, which the API
              // rejects as an unknown field.
              if (apiValue !== undefined) {
                node[key] = apiValue;
              }
              return node;
            }, {}) as unknown as any;

            if (row?.metadata) {
              mappedNode.metadata = row.metadata;
            }

            if (row?.identifier && persistedIdentifiers.has(row.identifier)) {
              mappedNode.identifier = row.identifier;
            }

            return mappedNode;
          });

          optimisticFieldValues[fieldDefinition.objectKey] = currentValue.map(
            (row) => {
              if (row?.identifier && persistedIdentifiers.has(row.identifier)) {
                return row;
              }

              const { identifier: _identifier, ...rest } = row;
              const optimisticRow = {
                ...rest,
                __typename: row.__typename ?? "TransactionObject",
              };

              if (!optimisticRow.id) {
                const { id, createdAt, updatedAt } =
                  generateMissingKeyValuesForOptimisticResponse(
                    "TransactionObject",
                  );
                optimisticRow.id = id;
                optimisticRow.createdAt = optimisticRow.createdAt ?? createdAt;
                optimisticRow.updatedAt = optimisticRow.updatedAt ?? updatedAt;
              }

              return optimisticRow;
            },
          );

          acc[fieldDefinition.inputKey] =
            fieldDefinition.listInputFormat === "array" ? nodes : { nodes };
        } else if (fieldDefinition.type === "address") {
          acc[fieldDefinition.inputKey] = currentValue;
        } else if (
          "hasExplicitGetValueForApi" in fieldDefinition &&
          fieldDefinition.hasExplicitGetValueForApi &&
          currentValue &&
          typeof currentValue === "object" &&
          !Array.isArray(currentValue)
        ) {
          Object.assign(acc, currentValue);
        } else {
          acc[fieldDefinition.inputKey] = currentValue;
        }
      }
      return acc;
    }, {});

    // ``__``-prefixed fields are GraphQL's own (``__typename``, which Apollo
    // attaches everywhere and input objects reject) and go at every level. A
    // single underscore is a client-side field: it is not an input field at the
    // root, but nested it lives inside free-form blobs — ``metadata`` carries
    // ``_frontend_uuid``, a row's stable identity from the moment it is
    // created, and flags the API itself reads such as
    // ``_tax_manual_override``. Stripping those meant a new row came back
    // without the id the client keyed it by, remounting the row and throwing
    // the user out of the cell they were editing.
    const cleanValues = (obj, isRoot = false) => {
      for (const key in obj) {
        if (key.startsWith("__") || (isRoot && key.startsWith("_"))) {
          delete obj[key];
        } else if (typeof obj[key] === "object") {
          cleanValues(obj[key]);
        }
      }
    };
    values && cleanValues(values, true);

    const nestedNode = inputKeyPath.reduceRight<Record<string, unknown>>(
      (acc, key) => ({ [key]: acc }),
      values as Record<string, unknown>,
    );

    // Returned so callers that need the created record can await it — the
    // inline "create new …" card in a reference picker assigns the result to
    // the field it was opened from.
    return mutate({
      variables: {
        input: {
          ...(nodeId &&
            mode !== "create" && {
              id: nodeId,
            }),
          node: nestedNode,
          ...(mode === "create" && {
            entityIdentifier:
              store.getState().entityIdentifier ??
              State.getState().getEntityIdentifier(),
          }),
        },
        startDate: State.getState().timeSeriesFilter.startDate?.toISOString(),
        endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
      },
      optimisticResponse:
        mode === "update"
          ? {
              updateNode: {
                __typename: "UpdateNodePayload",
                data: [optimisticFieldValues],
                errors: null,
              },
            }
          : undefined,
      onCompleted: (response: {
        updateNode?: { errors?: unknown } | null;
        createNode?: { errors?: unknown } | null;
      }) => {
        const payload = response?.updateNode ?? response?.createNode;
        if (payload?.errors) return;
        // Retire only this batch. Anything queued while the request was in
        // flight is still unsaved — clearing it (and treating the live values as
        // the new baseline) silently dropped edits made during the round trip,
        // so a line added mid-save vanished on the next reconcile.
        store.setState({
          fieldValuesInitial: {
            ...store.getState().fieldValuesInitial,
            ...submittedFieldValues,
          },
          pendingFieldOperations: store
            .getState()
            .pendingFieldOperations.filter(
              (operation) => operation.submitBatchId !== batchId,
            ),
        });
      },
    });
  }
}
